import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'react-flexbox-grid';
import { flatten, is, reverse, times } from 'ramda';
import moment, { isMoment } from 'moment';
import { formatDate } from 'lib/dateDisplay';

import withInfoBoxControls from '../containers/FloodlightVisualizationContainer';

import downArrow from '../assets/down-arrow.svg';

/*
  Component styles
*/

const wrapperStyles = {
  marginBottom: '2rem',
};

const headerStyles = {
  backgroundColor: '#A370D6',
  height: '5rem',
  borderRadius: '10px 10px 0 0',
  color: '#ffffff',
  padding: '0 1.4rem',
};

const infoBoxStyles = {
  backgroundColor: '#ffffff',
  padding: '15px 30px',
  border: '1px #ebebeb solid',
  borderTop: 'none',
  borderRadius: '0 0 3px 3px',
};

/*
  Graph settings
*/

// Layout
const sidePadding = 10;
const yLabelPadding = 20; // in px
const yLabelFontSize = 10;

// Number of elements to add to the end of range when calculating canvas size
const futurePaddingElements = 5;

// Static date domain
// Currently set as previous 90 days. The graph will only render data from this date range if used.
// StartOf and endOf should be used to avoid odd overlap issues.
const staticDateDomain = [moment().subtract(90, 'days').startOf('day'), moment().endOf('day')];

// Grid
const gridLineStrokeW = 2;
const graphGridGutterV = 13;

// Bars
const barStrokeWidth = 10;
const barGutter = 1;

// Dots
const dotStrokeWidth = 11;

// Ticks
const tickFontSize = 11;

/*
  Graph element styles
*/

const gridLineStyle = {
  strokeWidth: gridLineStrokeW,
  strokeDasharray: '5,5',
  stroke: '#e8e8e8',
};

const barLineStyle = {
  strokeWidth: barStrokeWidth,
  stroke: '#8c9395',
};

const yLabelStyle = {
  fontStyle: 'italic',
  fill: '#000',
  fontSize: yLabelFontSize + 'px',
  fontWeight: 600,
};

const tickLineStyle = {
  strokeWidth: 1,
  strokeDasharray: '5,3',
  stroke: '#606060',
};

const tickLabelStyle = {
  fill: '#42526e',
  fontSize: tickFontSize + 'px',
};

const featureButtonStyles = {
  width: '3.8rem',
  height: '3.8rem',
  borderRadius: '8px',
  backgroundColor: 'rgba(0,0,0,0.18)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
};

const featureButtonIconStyles = {
  width: '1.3rem',
  marginTop: '0.3rem',
};

const FeatureButton = ({ open, onShowInfoBox }) => (
  <div style={featureButtonStyles} onClick={onShowInfoBox}>
    <img style={{ ...featureButtonIconStyles, ...(open ? { transform: 'rotate(180deg)' } : {}) }} src={downArrow} />
  </div>
);

FeatureButton.propTypes = {
  open: PropTypes.bool,
  onShowInfoBox: PropTypes.func,
};

// const getValueVertPos = (grades, value) => reverse(grades).indexOf(value) * graphGridGutterV - gridLineStrokeW / 2;

const createDiscreteScaleY = grades => value => reverse(grades).indexOf(value) * graphGridGutterV - gridLineStrokeW / 2;
const createLinearScaleY = (values, scale) => {
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;
  return value => (1 - (value - min) / range) * scale * graphGridGutterV - gridLineStrokeW / 2;
};

const drawBar = (posX, yBottom, yTop, index, tooltip) => (
  <g key={index}>
    <title>{tooltip}</title>
    <line x1={posX} x2={posX} y1={yBottom} y2={yTop + barStrokeWidth * 0.75} style={barLineStyle} />
    <line
      x1={posX}
      x2={posX}
      y1={yTop + barStrokeWidth * 0.75}
      y2={yTop + barStrokeWidth * 0.75 - gridLineStrokeW}
      style={{ ...barLineStyle, strokeLinecap: 'round' }}
    />
  </g>
);

const drawPoint = (posX, yTop, key, tooltip) => (
  <g key={key}>
    <title>{tooltip}</title>
    <line
      x1={posX}
      x2={posX}
      y1={yTop}
      y2={yTop}
      style={{ ...barLineStyle, strokeWidth: dotStrokeWidth, strokeLinecap: 'round' }}
    />
  </g>
);

/**
 * Returns date domain (range of dates) from data
 * @param {*} data Floodlight data
 * @returns {moment[]} Array of moment objects with min and max dates in data in a tupple.
 */
const datesToDomain = data => {
  const dates = data.map(d => d.date);
  return [moment(Math.min(...dates)).startOf('day'), moment(Math.max(...dates)).endOf('day')];
};

const datesToRange = domain => {
  return [0, domain[1].diff(domain[0], 'days')];
};

const createScaleFunc = (domain, range) => datapoint => {
  if (!datapoint || !datapoint.date) return undefined;
  const date = isMoment(datapoint.date) ? datapoint.date : moment(datapoint.date);
  return Math.min(range[1], Math.max(range[0], date.diff(domain[0], 'days')));
};

const indexToDate = (domain, index) => {
  return domain[0].clone().add(index, 'd');
};

const generateDataSet = (scale, domain, range, data) => {
  const normalizedData = data.reduce((acc, d) => {
    const index = scale(d);
    return { ...acc, [index]: { ...d, index } };
  }, {});
  return times(i => {
    return normalizedData[i] || { index: i, date: indexToDate(domain, i), value: null };
  }, range[1] + 1);
};

const generateMonthTicks = (scale, domain) => {
  const domainMonths = domain[1].diff(domain[0].clone().startOf('month'), 'month');
  const firstMonthDays = domain[0].daysInMonth();
  const lastMonthDays = domain[1].daysInMonth();
  const maxDay = domain[1].clone();
  const minDay = domain[0].clone();

  const makeTickObject = date => {
    const type = date.date() === date.daysInMonth() ? 'END' : 'MID';
    return {
      date: type === 'END' ? date.add(1, 'd') : date,
      index: scale({ date }),
      type,
    };
  };

  const ticks = times(t => {
    let tickDates = [];

    // Handle first month
    if (t === 0) {
      if (Math.floor(firstMonthDays / 2) >= minDay.date()) {
        tickDates.push(makeTickObject(minDay.clone().date(Math.floor(firstMonthDays / 2))));
      }
      tickDates.push(makeTickObject(minDay.clone().endOf('month')));
      return tickDates;
    }

    // Handle last month
    if (t === domainMonths) {
      if (Math.floor(lastMonthDays / 2) <= maxDay.date()) {
        tickDates.push(makeTickObject(maxDay.clone().date(Math.floor(lastMonthDays / 2))));
      }
      if (lastMonthDays === maxDay.date()) {
        tickDates.push(makeTickObject(maxDay.clone().endOf('month')));
      }
      return tickDates;
    }

    // Handle regular full month
    const tickMonth = minDay.clone().add(t, 'month');
    tickDates.push(makeTickObject(tickMonth.clone().date(Math.floor(tickMonth.daysInMonth() / 2))));
    tickDates.push(makeTickObject(tickMonth.clone().endOf('month')));

    return tickDates;
  }, domainMonths + 1);
  return flatten(ticks);
};

const FloodlightVisualization = ({
  yLabel,
  featureName,
  featureIcon,
  headerColor,
  type = 'bars',
  scaleType = 'discrete',
  drawZeroLabel = false,
  data,
  showInfoBox = false,
  onShowInfoBox,
  labels,
  description,
  localeMessage,
}) => {
  // Grades
  const reversedGrades = reverse(labels);
  const numberOfGrades = labels.length + (!drawZeroLabel && 1);

  // Scales horizontal

  // Using the set static date domain here.
  // If it is desired to calculate the date domain from data, use datesToDomain(data) instead.
  const dateDomain = staticDateDomain;
  const dateRange = datesToRange(dateDomain);
  const scale = createScaleFunc(dateDomain, dateRange);
  const max = dateRange[1] + futurePaddingElements;

  // Scales vertical
  const getValueVertPos =
    scaleType === 'discrete' ? createDiscreteScaleY(labels) : createLinearScaleY(labels, labels.length - 1);

  // Normalize and pad data to a dataset
  const dataset = generateDataSet(scale, dateDomain, dateRange, data);

  // Generate month ticks
  const ticks = generateMonthTicks(scale, dateDomain);

  // Layout calculation
  const bottomGradeVertPos = (numberOfGrades - 1) * graphGridGutterV + gridLineStrokeW / 2;
  const canvasWidth = Math.max(1144, yLabelPadding + sidePadding + (barGutter + barStrokeWidth) * (max + 1));
  const barGap = barGutter + barStrokeWidth;

  // Label positioning
  const yLabelVertTransform = type === 'categories' ? yLabelFontSize : yLabelFontSize / 2;

  // L18n helper
  const localizeLabel =
    labels && labels.length > 0 && isNaN(labels[0]) ? l => localeMessage(`floodlight.datalabels.${l}`) : l => l;

  const localizeLabelTitle =
    labels && labels.length > 0 && isNaN(labels[0]) ? l => localeMessage(`floodlight.labelDescriptions.${l}`) : l => l;

  return (
    <Row style={wrapperStyles}>
      <Col xs={12}>
        <Row between="xs" middle="xs" style={{ ...headerStyles, ...{ backgroundColor: headerColor } }}>
          <Col xs={4} style={{ height: '100%' }}>
            <Row bottom="xs" style={{ height: '100%', fontSize: '1.1em' }}>
              <Col>{yLabel}</Col>
            </Row>
          </Col>
          <Col xsOffset={4} xs={4}>
            <Row end="xs" middle="xs">
              <Col xs={10}>
                <p style={{ fontSize: '1.3em', fontWeight: '600' }}>
                  <img style={{ verticalAlign: 'middle', height: '2.3rem', paddingRight: '0.5em' }} src={featureIcon} />
                  {featureName}
                </p>
              </Col>
              <Col xs={2}>
                <FeatureButton open={showInfoBox} onShowInfoBox={onShowInfoBox} />
              </Col>
            </Row>
          </Col>
        </Row>
      </Col>
      <Col xs={12} style={{ backgroundColor: '#ffffff', overflowX: 'scroll', padding: 0 }}>
        <div style={{ position: 'absolute', paddingLeft: 0, backgroundColor: '#ffffff' }}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            style={{ height: '12rem', width: sidePadding + yLabelPadding + 'px' }}
          >
            <g transform={`translate(${sidePadding + yLabelFontSize}, ${graphGridGutterV + yLabelVertTransform})`}>
              <g>
                {reversedGrades.map((g, i) => (
                  <text textAnchor="end" key={i} x={0} y={i * graphGridGutterV} style={yLabelStyle}>
                    {localizeLabel(g)}
                  </text>
                ))}
              </g>
            </g>
            <line
              x1={sidePadding + yLabelPadding - gridLineStrokeW}
              x2={sidePadding + yLabelPadding - gridLineStrokeW}
              y1={0}
              y2={160}
              style={{ ...gridLineStyle, strokeDasharray: '6,3' }}
            />
          </svg>
        </div>
        <svg xmlns="http://www.w3.org/2000/svg" style={{ height: '12rem', width: canvasWidth + 'px' }}>
          <g transform={`translate(${yLabelPadding + sidePadding}, ${graphGridGutterV})`}>
            <g>
              {times(
                n => (
                  <line
                    key={n}
                    x1="0"
                    y1={n * graphGridGutterV}
                    x2={canvasWidth}
                    y2={n * graphGridGutterV}
                    style={{ ...gridLineStyle, ...(type === 'categories' ? { strokeDasharray: '' } : {}) }}
                  />
                ),
                numberOfGrades,
              )}
            </g>
            <g>
              {dataset.map((d, i) => {
                if (!d.value) return null;

                if (is(Array, d.value) && d.value.length > 0) {
                  const xPos = dotStrokeWidth / 2 + barGap * i;
                  return d.value.map((point, pi) => {
                    const pointY = getValueVertPos(point) + dotStrokeWidth / 2 + gridLineStrokeW;
                    return drawPoint(xPos, pointY, `${i}_${pi}`, `${formatDate(d.date)}: ${localizeLabelTitle(point)}`);
                  });
                }

                const xPos = barStrokeWidth / 2 + barGap * i;
                return drawBar(
                  xPos,
                  bottomGradeVertPos,
                  getValueVertPos(d.value),
                  i,
                  `${formatDate(d.date)}: ${localizeLabelTitle(d.value)}`,
                );
              })}
            </g>
            <g transform={`translate(0, ${bottomGradeVertPos + gridLineStrokeW})`}>
              <line
                x1={0}
                x2={canvasWidth}
                y1={gridLineStrokeW + 1}
                y2={gridLineStrokeW + 1}
                style={{ stroke: '#FFFFFF', strokeWidth: 10 }}
              />
            </g>
            <g transform={`translate(0, ${bottomGradeVertPos + gridLineStrokeW})`}>
              {ticks.map(t => {
                const posX = barStrokeWidth + barGap * t.index;
                if (t.type === 'MID') {
                  return <line key={t.index} x1={posX} x2={posX} y1={0} y2={20} style={tickLineStyle} />;
                }
                return (
                  <React.Fragment key={t.index}>
                    <text
                      textAnchor="end"
                      x={posX - 10}
                      y={10 + tickFontSize}
                      style={{ ...tickLabelStyle, fontWeight: 600 }}
                    >
                      {t.date.format('MMM').toUpperCase()}
                    </text>
                    <text textAnchor="end" x={posX - 10} y={10 + tickFontSize * 2} style={tickLabelStyle}>
                      {t.date.year()}
                    </text>
                    <line key={t.index} x1={posX} x2={posX} y1={0} y2={40} style={tickLineStyle} />
                  </React.Fragment>
                );
              })}
            </g>
          </g>
        </svg>
      </Col>
      <Col xs={12} style={infoBoxStyles} hidden={!showInfoBox}>
        <Row>
          <Col xs={12}>
            <p className="bold">{localeMessage('floodlight.descriptionHeader')}</p>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <p dangerouslySetInnerHTML={{ __html: description }} />
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

FloodlightVisualization.propTypes = {
  yLabel: PropTypes.string.isRequired,
  featureName: PropTypes.string.isRequired,
  featureIcon: PropTypes.any.isRequired,
  headerColor: PropTypes.string.isRequired,
  data: PropTypes.any,
  type: PropTypes.string,
  scaleType: PropTypes.string,
  drawZeroLabel: PropTypes.bool,
  labels: PropTypes.any,
  showInfoBox: PropTypes.bool,
  onShowInfoBox: PropTypes.func,
  localeMessage: PropTypes.func,
  description: PropTypes.string,
};

export default withInfoBoxControls(FloodlightVisualization);
